import { create } from 'zustand';

// Monetization 360 — surfaces the backend's 402 spend/feature gates as a single
// global upgrade modal. The apiClient response interceptor calls `raise402`
// with the parsed error body; the modal (mounted once at the app root)
// subscribes and renders the right message + CTA. Kept tiny + dependency-free
// so the interceptor can import it without pulling in React.

export type BillingGateCode =
  | 'subscription_required'
  | 'cost_limit_exceeded'
  | 'token_limit_exceeded'
  | 'feature_not_in_plan'
  // TUFG seat scaling — seat-tier gates (creator-only Studio / collaborator+
  // thread+AI). Surfacing these pops an "assign a seat / upgrade" CTA instead
  // of a silent rejected promise.
  | 'creator_seat_required'
  | 'seat_capability_required'
  | 'seat_limit_reached';

export interface BillingGate {
  code: BillingGateCode | string;
  message: string;
  // Optional extras the backend attaches (used for richer copy).
  feature?: string;
  required_plan?: string;
  used_usd?: number;
  cap_usd?: number;
  used_tokens?: number;
  cap_tokens?: number;
  subscription_status?: string | null;
  // Seat-gate extras.
  need?: string;         // 'collaborator' (from seat_capability_required)
  seat_type?: string;    // which seat hit its cap (seat_limit_reached)
  limit?: number;
  current?: number;
}

interface BillingState {
  gate: BillingGate | null;
  raise402: (gate: BillingGate) => void;
  clear: () => void;
}

export const useBilling = create<BillingState>((set) => ({
  gate: null,
  raise402: (gate) => set({ gate }),
  clear: () => set({ gate: null }),
}));

// Imperative entry point for the non-React apiClient interceptor.
export function raiseBillingGate(gate: BillingGate) {
  useBilling.getState().raise402(gate);
}

// The set of 402 error codes that should pop the upgrade modal (vs. a normal
// rejected promise the caller handles itself).
const GATE_CODES = new Set<string>([
  'subscription_required',
  'cost_limit_exceeded',
  'token_limit_exceeded',
  'feature_not_in_plan',
  // TUFG seat-tier gates.
  'creator_seat_required',
  'seat_capability_required',
  'seat_limit_reached',
]);

export function isBillingGateCode(code?: string): boolean {
  return !!code && GATE_CODES.has(code);
}
